import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, Star, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../ui/alert-dialog';
import { fetchReviews, adminCreateReview, adminUpdateReview, adminDeleteReview } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const blank = { name: '', location: '', text: '', rating: 5, published: true };

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1 mt-1.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)}>
        <Star className={`h-6 w-6 ${n <= value ? 'fill-[#f2b705] text-[#f2b705]' : 'text-[#d8d6c9]'}`} />
      </button>
    ))}
  </div>
);

const ReviewsPanel = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await fetchReviews(true)); } catch (e) { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, location: r.location, text: r.text, rating: r.rating, published: r.published }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) { toast({ title: 'Name and review text are required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editing) await adminUpdateReview(editing.id, form);
      else await adminCreateReview(form);
      toast({ title: editing ? 'Review updated' : 'Review added' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await adminDeleteReview(id); toast({ title: 'Review deleted' }); load(); } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const togglePublish = async (r) => {
    setItems((prev) => prev.map((x) => (x.id === r.id ? { ...x, published: !x.published } : x)));
    try { await adminUpdateReview(r.id, { ...r, published: !r.published }); } catch (e) { load(); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-[#123020]">Reviews</h1>
          <p className="text-[#5a6b60] mt-1 text-[14px]">{items.length} review{items.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={openNew} className="bg-[#86c93c] hover:bg-[#75b52f] text-[#123020] font-semibold"><Plus className="mr-2 h-4 w-4" /> Add Review</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#5a6b60]"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-[#5a6b60]"><MessageSquare className="h-12 w-12 mb-3 text-[#a9b7a0]" /><p>No reviews yet.</p></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((r) => (
            <div key={r.id} className="bg-white rounded-md p-5 shadow-sm ring-1 ring-[#e6e4d7]">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#123020]">{r.name}</h3>
                    {!r.published && <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-500">Hidden</span>}
                  </div>
                  <p className="text-[13px] text-[#5f9e2c]">{r.location}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => togglePublish(r)} className="h-8 w-8 flex items-center justify-center rounded text-[#5a6b60] hover:bg-[#eef5e6]">{r.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                  <button onClick={() => openEdit(r)} className="h-8 w-8 flex items-center justify-center rounded text-[#5a6b60] hover:bg-[#eef5e6]"><Pencil className="h-4 w-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button className="h-8 w-8 flex items-center justify-center rounded text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete this review?</AlertDialogTitle><AlertDialogDescription>This review will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(r.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="flex gap-0.5 my-2">{[...Array(5)].map((_, i) => (<Star key={i} className={`h-4 w-4 ${i < r.rating ? 'fill-[#f2b705] text-[#f2b705]' : 'text-[#d8d6c9]'}`} />))}</div>
              <p className="text-[14px] text-[#41524a] italic">“{r.text}”</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[520px]">
          <DialogHeader><DialogTitle>{editing ? 'Edit Review' : 'Add Review'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-medium">Customer Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5" placeholder="John D." /></div>
              <div><Label className="font-medium">Location</Label><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="mt-1.5" placeholder="Manhattan, NY" /></div>
            </div>
            <div><Label className="font-medium">Rating</Label><StarPicker value={form.rating} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} /></div>
            <div><Label className="font-medium">Review Text</Label><Textarea value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} rows={4} className="mt-1.5 resize-none" placeholder="What did the customer say?" /></div>
            <div className="flex items-center justify-between rounded-md bg-[#f5f3ea] px-4 py-3">
              <div><p className="font-medium text-[#123020]">Published</p><p className="text-[12px] text-[#8a9980]">Show on the website</p></div>
              <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#86c93c] hover:bg-[#75b52f] text-[#123020] font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsPanel;
